import _jsx from "@babel/runtime/helpers/jsx";
import PropTypes from 'prop-types';
import React, { useCallback, useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import hasDom from 'has-dom';
import _debounce from 'lodash/debounce';
import Button from '../Button/index.js';
import Glyphicon from '../Glyphicon/index.js';

/**
 * SeeMore is a controlled component that handles the display and truncation of long content.  The content to be obscured should be passed to SeeMore as a child node.
 * When collapsed, a standard poriton of the wrapped content will be visible.  The component will also generate a trigger
 * at the bottom of the mask obscuring the child content.  Clicking this trigger will toggle the display of the mask. *
 * shouldCollapse controls the rendering of the collapse toggle based on the height of the child content.
 */
function SeeMore(_ref) {
  let {
    className,
    children,
    collapsed = {
      label: 'See More',
      glyph: 'menu-down'
    },
    collapsedSize,
    expanded = {
      label: 'See Less',
      glyph: 'menu-up'
    },
    initialCollapsed = true,
    disableFade = false,
    ...rest
  } = _ref;
  const [collapse, setCollapse] = useState(initialCollapsed);
  const [touched, setTouched] = useState(false);
  const [shouldCollapse, setShouldCollapse] = useState(true);
  const content = useRef(null);
  const seeMoreContainerRef = useRef(null);
  useEffect(() => {
    const checkHeight = () => {
      if (hasDom() && content && content.current) {
        // Only collapse if content exceeds container/scroll height by more than 50px to avoid showing unnecessary See more button
        setShouldCollapse(content.current.scrollHeight > content.current.clientHeight + 50);
      }
    };
    // Execute immediately on mount
    checkHeight();

    // Debounce and register/clean up listeners
    let resizeObserver;
    if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
      if (!seeMoreContainerRef.current) {
        return undefined;
      }
      const debouncedCheckHeight = _debounce(checkHeight, 100);
      resizeObserver = new ResizeObserver(debouncedCheckHeight);
      resizeObserver.observe(seeMoreContainerRef.current);
    }
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [content, setShouldCollapse, children]);
  const handleClick = useCallback(event => {
    setCollapse(!collapse);
    setTouched(true);
  }, [collapse]);
  const renderTrigger = () => {
    const {
      label,
      glyph
    } = collapse === false ? expanded : collapsed;
    return /*#__PURE__*/_jsx("div", {
      className: "display-flex justify-content-center margin-vertical-4"
    }, void 0, /*#__PURE__*/React.createElement(Button, Object.assign({}, rest, {
      onClick: handleClick,
      bsStyle: "text"
    }), label, /*#__PURE__*/_jsx(Glyphicon, {
      glyph: glyph,
      className: "margin-horizontal-2"
    })));
  };
  const rootClassName = clsx({
    'see-more': true,
    'see-more-collapse-sm': collapsedSize === 'sm',
    'see-more-collapse-md': collapsedSize === 'md',
    'see-more-collapse-lg': collapsedSize === 'lg'
  }, className);
  const contentClassName = clsx({
    'see-more-content': true,
    collapse
  });
  const maskClassName = clsx({
    'see-more-fade': true
  });

  /*
   * show trigger if the shouldCollapse calculation on page load determines it should have one,
   * once it has a trigger it should remain having a trigger once it is touched/interacted with
   */
  return /*#__PURE__*/React.createElement("div", Object.assign({
    "data-cmp": "seeMore",
    ref: seeMoreContainerRef
  }, rest, {
    className: rootClassName
  }), /*#__PURE__*/React.createElement("div", {
    ref: content,
    className: contentClassName
  }, /*#__PURE__*/_jsx("div", {}, void 0, children), shouldCollapse && !disableFade && /*#__PURE__*/_jsx("div", {
    className: maskClassName
  })), (shouldCollapse || touched) && renderTrigger());
}
export default SeeMore;